import { useState } from "react";
import {
    Title, Stack, Card, Table, Badge, Text, Button, Group,
    ActionIcon, Menu,
} from "@mantine/core";
import { IconDots, IconPencil, IconKey, IconUserOff, IconUserCheck } from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import type { User } from "@iso27001/shared";
import { usersApi } from "../../api/users.js";
import { useAuth } from "../../auth/AuthContext.js";
import { useApiErrorMessage } from "../../i18n/api-error.js";
import { useEnumLabel } from "../../i18n/enum-label.js";
import { useRecordDialog } from "../../components/use-record-dialog.js";
import { rowOpenProps, stopRowOpen } from "../../components/list-table.js";
import { UserFormModal } from "./UserFormModal.js";
import { UserPasswordResetModal } from "./UserPasswordResetModal.js";

// ── Constants ─────────────────────────────────────────────────────────────────

const ROLE_COLOR: Record<User["role"], string> = {
    admin: "red",
    editor: "yellow",
    viewer: "gray",
};

const CLEARANCE_COLOR: Record<User["clearance"], string> = {
    öffentlich: "gray",
    intern: "blue",
    vertraulich: "orange",
};

// ── Row component ─────────────────────────────────────────────────────────────

interface RowProps {
    user: User;
    currentUserId: number | undefined;
    onEdit: (u: User) => void;
    onResetPassword: (u: User) => void;
    onToggleActive: (u: User) => void;
    togglePending: boolean;
}

function UserRow({ user, currentUserId, onEdit, onResetPassword, onToggleActive, togglePending }: RowProps) {
    const { t } = useTranslation();
    const { label } = useEnumLabel();
    return (
        <Table.Tr key={user.id} opacity={user.active ? 1 : 0.55} {...rowOpenProps(() => onEdit(user))}>
            <Table.Td>
                <Text size="sm" fw={500}>{user.name}</Text>
            </Table.Td>
            <Table.Td>
                <Text size="sm" c="dimmed">{user.email}</Text>
            </Table.Td>
            <Table.Td>
                <Badge color={ROLE_COLOR[user.role]} variant="light">
                    {label("role", user.role)}
                </Badge>
            </Table.Td>
            <Table.Td>
                <Badge color={CLEARANCE_COLOR[user.clearance]} variant="light">
                    {label("clearance", user.clearance)}
                </Badge>
            </Table.Td>
            <Table.Td>
                <Text size="sm">{user.initials ?? "–"}</Text>
            </Table.Td>
            <Table.Td>
                <Badge color={user.active ? "green" : "gray"} variant="light">
                    {user.active ? t("users.status.active") : t("users.status.inactive")}
                </Badge>
            </Table.Td>
            <Table.Td {...stopRowOpen}>
                <Menu position="bottom-end" withinPortal>
                    <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" disabled={togglePending} aria-label={t("common.action.actions")}>
                            <IconDots size={16} />
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => onEdit(user)}>
                            {t("common.action.edit")}
                        </Menu.Item>
                        <Menu.Item leftSection={<IconKey size={14} />} onClick={() => onResetPassword(user)}>
                            {t("users.list.menu.resetPassword")}
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                            leftSection={user.active ? <IconUserOff size={14} /> : <IconUserCheck size={14} />}
                            color={user.active ? "red" : "teal"}
                            disabled={user.id === currentUserId}
                            onClick={() => onToggleActive(user)}
                        >
                            {user.active ? t("users.list.menu.deactivate") : t("users.list.menu.activate")}
                        </Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            </Table.Td>
        </Table.Tr>
    );
}

// ── Page component ────────────────────────────────────────────────────────────

export function UsersListPage() {
    const { user: currentUser } = useAuth();
    const qc = useQueryClient();
    const apiError = useApiErrorMessage();
    const { t } = useTranslation();

    const dialog = useRecordDialog<User>();
    const [resetTarget, setResetTarget] = useState<User | null>(null);

    const { data, isLoading } = useQuery({
        queryKey: ["users-all"],
        queryFn: () => usersApi.listAll(),
    });

    const toggleActive = useMutation({
        mutationFn: (u: User) => usersApi.update(u.id, { active: !u.active }),
        onSuccess: (_data, u) => {
            qc.invalidateQueries({ queryKey: ["users-all"] });
            notifications.show({
                message: u.active
                    ? t("users.list.notify.deactivated", { name: u.name })
                    : t("users.list.notify.activated", { name: u.name }),
                color: u.active ? "orange" : "teal",
            });
        },
        onError: (e: unknown) => {
            // Conflict here means either "you cannot deactivate your own account"
            // or "cannot deactivate the last admin" (apps/api/src/modules/users/routes.ts)
            // — the code can't distinguish them, so the text doesn't try to either.
            notifications.show({
                message: apiError(e, { Conflict: "users.error.cannotDeactivate", fallback: "users.error.saveFailed" }),
                color: "red",
            });
        },
    });

    // Defense in depth: non-admins see a permission denied message
    if (currentUser && currentUser.role !== "admin") {
        return (
            <Stack>
                <Title order={2}>{t("users.list.title")}</Title>
                <Text c="dimmed">{t("users.list.forbidden")}</Text>
            </Stack>
        );
    }

    const items = data?.items ?? [];

    const openCreate = () => dialog.open(null);
    const openEdit = (u: User) => dialog.open(u);

    return (
        <Stack>
            <Group justify="space-between" align="center">
                <Title order={2}>{t("users.list.title")}</Title>
                <Button onClick={openCreate}>{t("users.newUser")}</Button>
            </Group>

            <Card withBorder padding={0}>
                <Table striped highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>{t("common.field.name")}</Table.Th>
                            <Table.Th>{t("users.field.email")}</Table.Th>
                            <Table.Th style={{ width: 100 }}>{t("users.field.role")}</Table.Th>
                            <Table.Th style={{ width: 120 }}>{t("users.field.clearance")}</Table.Th>
                            <Table.Th style={{ width: 90 }}>{t("users.field.initials")}</Table.Th>
                            <Table.Th style={{ width: 90 }}>{t("users.field.active")}</Table.Th>
                            <Table.Th style={{ width: 50 }} />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {isLoading && (
                            <Table.Tr>
                                <Table.Td colSpan={7}>
                                    <Text ta="center" c="dimmed" py="md">{t("users.list.loading")}</Text>
                                </Table.Td>
                            </Table.Tr>
                        )}
                        {!isLoading && items.length === 0 && (
                            <Table.Tr>
                                <Table.Td colSpan={7}>
                                    <Text ta="center" c="dimmed" py="md">{t("users.list.empty")}</Text>
                                </Table.Td>
                            </Table.Tr>
                        )}
                        {items.map((u) => (
                            <UserRow
                                key={u.id}
                                user={u}
                                currentUserId={currentUser?.id}
                                onEdit={openEdit}
                                onResetPassword={setResetTarget}
                                onToggleActive={(target) => toggleActive.mutate(target)}
                                togglePending={toggleActive.isPending}
                            />
                        ))}
                    </Table.Tbody>
                </Table>
            </Card>

            <UserFormModal
                key={dialog.key}
                opened={dialog.opened}
                onClose={dialog.close}
                initial={dialog.record}
            />

            {resetTarget && (
                <UserPasswordResetModal
                    opened={!!resetTarget}
                    onClose={() => setResetTarget(null)}
                    userId={resetTarget.id}
                    userName={resetTarget.name}
                />
            )}
        </Stack>
    );
}
